
/**
 * RiskReports Integration Layer
 * Connects SDK Service to Zustand Store
 */
import i18next from 'i18next';
import { toast } from 'sonner';
import { useRiskReportsStore } from '../../store/risk-reports.store.js';
import { Logger } from '@/lib/app-log.js';

export class RiskReportsIntegration {
    constructor(sdkService) {
        this.service = sdkService;
    }

    /**
     * Fetch all items and update store
     */
    async fetchRiskReports(params = {}) {
        const store = useRiskReportsStore.getState();
        
        if (store.setLoading) store.setLoading(true);
        if (store.clearError) store.clearError();

        try {
            const response = await this.service.getAll(params);
            
            // Handle various setter naming conventions
            if (store.setRiskReports) {
                store.setRiskReports(response.data);
            } else if (store.setItems) {
                store.setItems(response.data);
            } else if (store.setRiskReportss) { // Fallback standard
                store.setRiskReportss(response.data);
            }
            
            // Handle Pagination
            if (response.meta || response.pagination) {
                const pagination = response.meta || response.pagination;
                if (store.setPagination) store.setPagination(pagination);
                else if (store.setPaginationData) store.setPaginationData(pagination);
                else if (store.setParams) store.setParams({ pagination });
            }

            // Handle Unique Values (if applicable)
            if (response.uniqueValues && store.setUniqueValues) {
                store.setUniqueValues(response.uniqueValues);
            }

            return { success: true, data: response.data };
        } catch (error) {
            const message = this._getErrorMessage(error);
            if (store.setError) store.setError(message);
            toast.error(message);
            return { success: false, error: message };
        } finally {
            if (store.setLoading) store.setLoading(false);
        }
    }

    /**
     * Get by ID
     */
    async fetchRiskReportsById(id) {
        const store = useRiskReportsStore.getState();
        if (store.setLoading) store.setLoading(true);

        try {
            const response = await this.service.getById(id);
            
            // Try common naming for selecting single item
            if (store.setSelectedRiskReports) {
                store.setSelectedRiskReports(response.data);
            } else if (store.setCurrentRiskReports) {
                store.setCurrentRiskReports(response.data);
            } else if (store.setSelectedItem) {
                store.setSelectedItem(response.data);
            }

            return { success: true, data: response.data };
        } catch (error) {
            const message = this._getErrorMessage(error);
            toast.error(message);
            return { success: false, error: message };
        } finally {
            if (store.setLoading) store.setLoading(false);
        }
    }

    /**
     * Create item
     */
    async createRiskReports(data) {
        const store = useRiskReportsStore.getState();
        if (store.setLoading) store.setLoading(true);

        try {
            const response = await this.service.create(data);
            
            toast.success(i18next.t('integrationToasts:riskreportsCreated', { defaultValue: 'RiskReports created' }));
            
            // Optional: Close form if store has that action
            if (store.closeForm) store.closeForm();
            if (store.cancelEditing) store.cancelEditing();

            return { success: true, data: response.data };
        } catch (error) {
            const message = this._getErrorMessage(error);
            toast.error(message);
            return { success: false, error: message };
        } finally {
            if (store.setLoading) store.setLoading(false);
        }
    }

    /**
     * Update item
     */
    async updateRiskReports(id, data) {
        const store = useRiskReportsStore.getState();
        if (store.setLoading) store.setLoading(true);

        try {
            const response = await this.service.update(id, data);
            
            toast.success(i18next.t('integrationToasts:riskreportsUpdated', { defaultValue: 'RiskReports updated' }));
            
            if (store.closeForm) store.closeForm();
            if (store.cancelEditing) store.cancelEditing();

            return { success: true, data: response.data };
        } catch (error) {
            const message = this._getErrorMessage(error);
            toast.error(message);
            return { success: false, error: message };
        } finally {
            if (store.setLoading) store.setLoading(false);
        }
    }

    /**
     * Delete item
     */
    async deleteRiskReports(id) {
        const store = useRiskReportsStore.getState();
        if (store.setLoading) store.setLoading(true);

        try {
            const response = await this.service.delete(id);
            
            toast.success(i18next.t('integrationToasts:riskreportsDeleted', { defaultValue: 'RiskReports deleted' }));
            
            if (store.closeDeleteDialog) store.closeDeleteDialog();

            return { success: true };
        } catch (error) {
            const message = this._getErrorMessage(error);
            toast.error(message);
            return { success: false, error: message };
        } finally {
            if (store.setLoading) store.setLoading(false);
        }
    }

    _getErrorMessage(error) {
         if (error.isValidationError?.()) {
            return 'Please check the form data';
        }
        return error.message || 'An error occurred';
    }
}
